import type { RoundEntry, Score, Sex } from '../model/types.js';

/** One player line (code 001) of a TRF file. */
export interface TrfPlayer {
  /** Starting rank / pairing number (1-based). */
  readonly startRank: number;
  readonly sex: Sex | null;
  readonly title: string;
  readonly name: string;
  readonly rating: number;
  readonly federation: string;
  readonly fideId: string;
  readonly birthDate: string;
  /** Points as written in the file (tenths), NaN when blank. */
  readonly points: Score;
  /** Rank as written in the file, 0 when blank. */
  readonly rank: number;
  readonly rounds: readonly RoundEntry[];
}

/** A team line (013 in TRF-16; 310 in TRF26 carries more fields). */
export interface TrfTeam {
  readonly name: string;
  readonly members: readonly number[];
  /** TRF26 310 fields (absent for 013 lines). */
  readonly pairingNumber?: number;
  readonly nickname?: string;
  readonly matchPoints?: Score;
  readonly gamePoints?: Score;
  readonly rank?: number;
}

/**
 * Acceleration. TRF(x) XXA lines give one player's virtual points per round;
 * TRF26 250 lines give points for a range of rounds and a range of players.
 * Both are kept as ranges here: XXA becomes one range per round.
 */
export interface TrfAcceleration {
  /** Fictitious match points (team events), tenths. */
  readonly matchPoints: Score;
  /** Fictitious (game) points, tenths. */
  readonly points: Score;
  readonly firstRound: number;
  readonly lastRound: number;
  readonly firstId: number;
  readonly lastId: number;
}

/** Requested or known byes (TRF26 240): kind F/H/Z, or null if unspecified (individuals). */
export interface TrfBye {
  readonly kind: 'F' | 'H' | 'Z' | null;
  readonly round: number;
  readonly ids: readonly number[];
}

/** Prohibited pairings (TRF26 260, legacy XXP): none of `ids` may meet in the round range. */
export interface TrfForbidden {
  readonly firstRound: number;
  readonly lastRound: number;
  readonly ids: readonly number[];
}

/** Abnormal points assignment (TRF26 299). */
export interface TrfAbnormalPoints {
  /** Result type W, D, L, F, H, Z, +, - or '' for a penalty/bonus. */
  readonly type: string;
  readonly matchPoints: Score | null;
  readonly points: Score | null;
  /** 0 = all rounds. */
  readonly round: number;
  /** Empty = all. */
  readonly ids: readonly number[];
}

/** National Rating Support record (TRF26): national data for a player. */
export interface TrfNationalRating {
  readonly federation: string;
  readonly startRank: number;
  readonly sex: string;
  readonly classification: string;
  readonly name: string;
  readonly rating: number;
  readonly origin: string;
  readonly nationalId: string;
  readonly birthDate: string;
}

/**
 * Scoring overrides (TRF26 162; legacy XXS and BB* lines), tenths.
 * W win (OTB, forfeit, FPB), D draw (and HPB), L loss OTB, A absence (ZPB or
 * forfeit loss), P pairing-allocated bye, X unknown result (e.g. adjourned).
 */
export type TrfScoreKey = 'W' | 'D' | 'L' | 'A' | 'P' | 'X';

export interface TrfDocument {
  /** Header fields by code ("012" name, "022" city, ...), raw text after the code. */
  readonly headers: ReadonlyMap<string, string>;
  readonly players: readonly TrfPlayer[];
  readonly teams: readonly TrfTeam[];
  /** Total number of rounds (XXR / 142), when given. */
  readonly totalRounds: number | null;
  /** Initial colour (152, or XXC white1/black1), when given. */
  readonly initialColour: 'w' | 'b' | null;
  /** XXC rank: pair by the file's rank order instead of starting rank. */
  readonly useRankAsPairingId: boolean;
  readonly accelerations: readonly TrfAcceleration[];
  readonly scoring: Partial<Record<TrfScoreKey, Score>>;
  /** Encoded tournament type (192), e.g. FIDE_DUTCH_2026. */
  readonly tournamentType: string | null;
  /** Tie-break list (202) as MTB26 descriptors. */
  readonly tiebreaks: readonly string[];
  /** Full standings definition (212), starting with PTS. */
  readonly standings: readonly string[];
  readonly byes: readonly TrfBye[];
  readonly forbidden: readonly TrfForbidden[];
  readonly abnormalPoints: readonly TrfAbnormalPoints[];
  readonly nationalRatings: readonly TrfNationalRating[];
  /** Players absent from the next round (JaVaFo XXZ). */
  readonly absentNextRound: readonly number[];
  /** Lines the parser did not interpret, kept for round-tripping. */
  readonly otherLines: readonly string[];
}

/** An empty document, handy as a base for builders. */
export function emptyTrf(): TrfDocument {
  return {
    headers: new Map(),
    players: [],
    teams: [],
    totalRounds: null,
    initialColour: null,
    useRankAsPairingId: false,
    accelerations: [],
    scoring: {},
    tournamentType: null,
    tiebreaks: [],
    standings: [],
    byes: [],
    forbidden: [],
    abnormalPoints: [],
    nationalRatings: [],
    absentNextRound: [],
    otherLines: [],
  };
}
